import React, { useState } from 'react';
import { CatalogProduct } from '../catalogTypes';
import { isFavoriteSku, toggleFavoriteSku } from '../../../../utils/favoritesStore';
import styles from './ProductCard.module.css';

interface ProductCardProps {
  product: CatalogProduct;
  onAddToCart?: (productId: string, quantity: number) => void;
  onFavoriteChange?: () => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart, onFavoriteChange }) => {
  const isOutOfStock = product.status === 'out-of-stock';
  const [qty, setQty] = useState(1);
  const [isFavorite, setIsFavorite] = useState(() => isFavoriteSku(product.id));

  const handleFavoriteToggle = () => {
    const nowFavorite = toggleFavoriteSku(product.id);
    setIsFavorite(nowFavorite);
    onFavoriteChange?.();
  };

  return (
    <div className={styles.card}>
      <div className={`${styles.imageWrapper} ${isOutOfStock ? styles.outOfStockImage : ''}`}>
        {product.image ? (
          <img src={product.image} alt={product.name} className={styles.image} />
        ) : (
          <div className={styles.placeholder} />
        )}
        {isOutOfStock && (
          <span className={styles.outOfStockBadge}>Out Of Stock</span>
        )}
        <div className={styles.badgeRow}>
          {product.isDeal && <span className={`${styles.badge} ${styles.badgeDeal}`}>Deal</span>}
          {product.isNew && <span className={`${styles.badge} ${styles.badgeNew}`}>New</span>}
        </div>
        <button
          type="button"
          className={`${styles.favoriteBtn} ${isFavorite ? styles.favoriteActive : ''}`}
          onClick={handleFavoriteToggle}
          aria-pressed={isFavorite}
          aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          <svg viewBox="0 0 24 24" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" width="16" height="16">
            <path d="M12 21s-6.716-4.35-9.428-8.06C.86 10.27 1.2 6.6 4.1 4.9c2.2-1.28 4.86-.63 6.4 1.33.5.63.9 1.3 1.5 1.3s1-.67 1.5-1.3c1.54-1.96 4.2-2.61 6.4-1.33 2.9 1.7 3.24 5.37 1.53 8.04C18.716 16.65 12 21 12 21z" />
          </svg>
        </button>
      </div>
      <div className={styles.details}>
        <h3 className={styles.name}>{product.name}</h3>
        <p className={styles.meta}>SKU: {product.sku}</p>
        <p className={styles.meta}>Pack Size: {product.packSize}</p>
        {product.isPreviouslyOrdered && (
          <p className={styles.meta} style={{ color: '#2563EB', fontWeight: 600 }}>Previously ordered</p>
        )}
        <div className={styles.footer}>
          <div className={styles.pricing}>
            <span className={styles.price}>${product.price.toFixed(2)}</span>
            {product.originalPrice != null && product.originalPrice > product.price && (
              <span className={styles.originalPrice}>${product.originalPrice.toFixed(2)}</span>
            )}
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.qtyStepper}>
            <button
              type="button"
              className={styles.qtyBtn}
              disabled={isOutOfStock || qty <= 1}
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              aria-label="Decrease quantity"
            >
              −
            </button>
            <span className={styles.qtyValue}>{qty}</span>
            <button
              type="button"
              className={styles.qtyBtn}
              disabled={isOutOfStock}
              onClick={() => setQty((q) => q + 1)}
              aria-label="Increase quantity"
            >
              +
            </button>
          </div>
          <button
            className={styles.addToCartBtn}
            onClick={() => {
              onAddToCart?.(product.id, qty);
              setQty(1);
            }}
            disabled={isOutOfStock}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className={styles.cartIcon}>
              <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};
